import { parseVAST, type VASTResult } from "./vast-parser"

/**
 * Resolving a video preroll (pick a zone → verify live fill → fetch/parse
 * VAST, possibly through a wrapper redirect) takes real time — a user
 * reported this as the preroll visibly hanging on "Loading ad..." before
 * dismissing. All of that work can happen while the user is still browsing
 * the grid, before they've clicked into any video, so by the time a preroll
 * is actually needed it's already resolved.
 */

interface PrerollCacheEntry {
  vastUrl: string
  result: VASTResult | null
  resolvedAt: number
  /** ad_zones UUID. The VAST layer reports to the NETWORK via the creative's
   *  own tracking pixels, but our internal ad_impressions had no record of
   *  preroll at all, so video showed as zero in our own reporting. */
  zoneId: string | null
  tier: number
}

let cache: PrerollCacheEntry | null = null
let inflight: Promise<void> | null = null

// VAST tracking/click-through URLs are effectively single-use per ad
// request — holding a resolved ad this long without ever showing it would
// mean firing impression trackers for a view that never happened, so a stale
// entry is dropped rather than served.
const MAX_AGE_MS = 45_000

async function resolve(tier: number): Promise<void> {
  try {
    const res = await fetch(`/api/ads/network-grid-ad?type=video&tier=${tier}`)
    if (!res.ok) return
    const d = await res.json()
    if (!d?.zone_id || !d?.vast_url) return
    const result = await parseVAST(d.vast_url)
    cache = { vastUrl: d.vast_url, result, resolvedAt: Date.now(), zoneId: d.zone_id ?? null, tier }
  } catch {
    // Leave cache empty — the caller falls back to resolving fresh.
  }
}

/** Kick off resolving a preroll ad in the background. Safe to call
 * repeatedly (e.g. once per grid mount) — no-ops if a fresh one is already
 * cached or a resolution is already in flight. */
export function warmVideoPreroll(tier = 3) {
  if (cache && cache.tier === tier && Date.now() - cache.resolvedAt < MAX_AGE_MS) return
  if (inflight) return
  inflight = resolve(tier).finally(() => {
    inflight = null
  })
}

/** Consume the pre-warmed ad if one is ready and still fresh. Returns null
 * if nothing was ready in time (too slow, too stale, or never warmed) —
 * the caller should fall back to resolving fresh in that case. Always
 * clears the cache so a given resolved ad is only ever handed out once. */
export function takeWarmedPreroll(tier = 3): { vastUrl: string; result: VASTResult | null; zoneId: string | null } | null {
  if (!cache || cache.tier !== tier || Date.now() - cache.resolvedAt >= MAX_AGE_MS) {
    cache = null
    return null
  }
  const { vastUrl, result, zoneId } = cache
  cache = null
  return { vastUrl, result, zoneId }
}
